/**
 * Settings -> Integrations: the Wordfence Intelligence key behind Saidiseire's
 * vulnerability feed, and where the feed stands
 * (docs/saidiseire-kiirus-turve-raport.md, item B).
 *
 * The feed is free, also for commercial use, and needs a free key from a
 * wordfence.com account. Once a day the engine downloads it (about 120 MB)
 * and matches every covered site's WordPress, plugins and themes against it
 * on our own server. Without a key no security scan runs, and the site pages
 * say so.
 *
 * Besides the key the panel says what the feed last did: when it was stored,
 * how many records it holds, and why the last attempt failed, because a
 * revoked key otherwise shows up only as scans that quietly stop. The pill in
 * the head says "ready" only when a key is stored, the feed was downloaded and
 * the last attempt did not fail (`wordfenceStatus`); the PageSpeed card's pill
 * is about its key alone, since nothing is downloaded there.
 *
 * The licence line is part of the deal, not decoration: Wordfence's licence
 * requires the record link and its copyright and licence text wherever a
 * vulnerability is shown, and this is where staff learn that.
 */
import { ExternalLink, ShieldCheck } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { feedErrorKey, wordfenceStatus } from "../../../../lib/monitoringKeys";
import { StatusPill, fmtDateTime, fmtNum } from "../../../ui";
import { IntegrationCard, PanelLoader } from "../panel/IntegrationCard";
import { Note } from "../panel/fields";
import { MonitorKeyForm, useMonitorKeys } from "./MonitorKeyForm";

/** Wordfence's own terms for the Intelligence feed, which the licence line points to. */
const TERMS_URL = "https://www.wordfence.com/wordfence-intelligence-terms-and-conditions/";

export function WordfenceCard() {
  const t = useT();
  const { view, loading, setView } = useMonitorKeys();

  if (loading) return <PanelLoader />;
  if (!view) return null;

  const feed = view.wordfence.feed;
  const pill = wordfenceStatus(view.wordfence);
  const errorKey = feedErrorKey(feed.error);
  const errorText = feed.error ? (errorKey ? t(`settings.wordfence.feedErrors.${errorKey}`) : feed.error) : null;

  return (
    <IntegrationCard
      icon={ShieldCheck}
      title={t("settings.wordfence.title")}
      desc={t("settings.wordfence.desc")}
      status={<StatusPill tone={pill.tone}>{t(`settings.wordfence.status.${pill.status}`)}</StatusPill>}
    >
      <Note tone="info">{t("settings.wordfence.what")}</Note>
      <p className="crm-sub mb-3 text-[13px]">{t("settings.wordfence.where")}</p>

      <MonitorKeyForm name="wordfence" keySet={view.wordfence.keySet} onSaved={setView} />

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <StatusPill tone={feed.fetchedAt ? "ok" : "mut"}>
          {feed.fetchedAt ? t("settings.wordfence.feedFetched", { when: fmtDateTime(feed.fetchedAt) }) : t("settings.wordfence.feedNever")}
        </StatusPill>
        {feed.fetchedAt ? <StatusPill tone="mut">{t("settings.wordfence.feedEntries", { count: fmtNum(feed.entries) })}</StatusPill> : null}
      </div>
      {/* A sentence, not a pill: a pill never wraps, and the error is a whole sentence that must fit a phone. */}
      {errorText ? (
        <p className="mt-2 break-words text-[13px] text-[var(--color-destructive)]">{t("settings.wordfence.feedError", { error: errorText })}</p>
      ) : null}

      <p className="crm-sub mt-4 text-[13px]">
        {t("settings.wordfence.licence")}{" "}
        <a href={TERMS_URL} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1 underline">
          {t("settings.wordfence.terms")}
          <ExternalLink className="size-3" />
        </a>
      </p>
    </IntegrationCard>
  );
}
